import { LocalDate } from "@js-joda/core";
import * as c from "purify-ts/Codec";
import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dynStyle } from "trader.ts/ui";
import { get_students_full } from "../../../sql/classy/lessons";
import {
  iban,
  phone_type,
  studentid,
  thirdpartysellerid,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import { arrayFilter, lens, mkCompositeFilter } from "../../client/filterbuild";
import {
  classyFiltersImpl,
  renderFiltersSaveAndLoadButtons,
} from "../../client/filtersaveloadbuttons";
import { groupByIntoArrays } from "../../lib/groupbyintoarrays";
import { isNil } from "../../lib/istype";
import {
  getFromLocalStorage,
  localStorageSpec,
  setLocalStorage,
} from "../../lib/localstorage";
import { calculateAge } from "../../lib/time";
import { parseWeekday } from "../../parsing_from_ur";
import {
  ddMMyyyy,
  renderEnrollmentStatus,
  renderTeacherPlusTimePlusWeekday,
  wrapInParenthesesIfNotEmpty,
} from "../../render";
import * as routes from "../../routes";
import { renderPeriod } from "../../translations_specific";
import {
  mkEnrollmentFilter,
  mkStudentFilterImpl,
} from "../enrollments_filters_advanced/client";
import { advancedStudentFiltersData } from "../groupmessage/client";
import { getCurrentOrNextPeriod } from "../../domain/periods";
import { Remote } from "trader.ts";
import { rpcIntoRemote } from "trader.ts/network.remote";
import { renderRemote } from "trader.ts/ui.common";

type enrollment = Awaited<ReturnType<typeof get_students_full>>[number];
type student = {
  firstname: string;
  lastname: string;
  id: studentid;
  street: string;
  number: string;
  bus: string;
  zip: string;
  city: string;
  comments: string;
  birthday: LocalDate | null;
  iban: iban | null;
  name_on_iban: string;
  thirdpartysellerid: thirdpartysellerid | null;
  phones:
    | {
        number: string;
        description: phone_type | null;
      }[]
    | null;
  emails:
    | {
        email: string;
      }[]
    | null;
  enrollments: enrollment[];
};

const filterLs: localStorageSpec<string> = {
  key: "classy-advanced-students-filter",
  codec: c.string,
};

export function render(opts: {
  data: advancedStudentFiltersData;
}): HTMLElement | HTMLElement[] {
  const updateS = new Source(true);

  const today = LocalDate.now();

  const enrollmentsRS: Source<Remote.Remote<enrollment[]>> = new Source({
    tag: "initial",
  });
  void rpcIntoRemote(
    routes.getAllEnrollmentsSpec,
    undefined,
    {},
    enrollmentsRS,
  );

  const studentFilter = mkCompositeFilter<student>({
    ...mkStudentFilterImpl(today, opts.data),
    enrollment: {
      label: "Inschrijvingen",
      mkFilter: () =>
        // stu.enrollments is an array of enrollments
        // mkEnrollmentFilter works on a single enrollment
        lens(
          (stu) => stu.enrollments,
          arrayFilter(mkEnrollmentFilter(opts.data, false)()),
        ),
    },
  });
  studentFilter.observe(() => {
    updateS.set(!updateS.get());
    setLocalStorage(filterLs, JSON.stringify(studentFilter.serializeState()));
  });

  const fromLs = getFromLocalStorage(filterLs);
  if (fromLs) {
    studentFilter.applySerializedState(JSON.parse(fromLs));
  } else {
    // TODO: this id not great, this should be typed
    const currentPeriod = getCurrentOrNextPeriod(today, opts.data.periods);
    studentFilter.applySerializedState({
      operator: "and",
      filters: currentPeriod
        ? [
            {
              key: "enrollment",
              filter: {
                single: {
                  operator: "and",
                  filters: [
                    {
                      key: "period",
                      filter: {
                        ids: [currentPeriod.id.toString()],
                        op: "eq",
                      },
                    },
                  ],
                },
                op: "includes",
              },
            },
          ]
        : [],
    });
  }

  const buttons = renderFiltersSaveAndLoadButtons(
    classyFiltersImpl,
    studentFilter,
    "Student",
    "studenten",
  );

  const tableid = "classy-table-id";
  const downloadLinks = renderTableDownloadLinks({
    tableid,
    filename: "Overzicht studenten",
    replaceNewlinesWithCommas: true,
    removeDisplayNoneRows: true,
    xlsLabel:
      "Download als xls" +
      " (" +
      "Bevat alle studentengegevens" +
      ")",
    csvLabel:
      "Download als csv" +
      " (" +
      "Bevat alle studentengegevens" +
      ")",
  });

  return (
    <main style="max-width: unset;">
      <div className={style.card + style.card_large}>
        <h1 style="margin: -8px 0px 32px 0px; font-size: 18px;">
          {"Zoek studenten"}
        </h1>
        <article>{studentFilter.render()}</article>
        <div>{buttons}</div>
      </div>

      <section className={style.card + style.card_large}>
        <div style="text-align: right;">{downloadLinks}</div>

        <table id={tableid}>
          {
            // TODO DOESN'T WORK NOW, FILTERED BY removeDisplayNoneRows: true,
            renderStudentAdvancedHeaderForExport({
              tag: "grouped_enrollments",
            })
          }
          {renderRemote(enrollmentsRS, (enrollments) => {
            const groupedStudents = groupByIntoArrays(
              enrollments,
              (stu) => stu.student.id,
            ).map(([_, enrs]) => ({
              ...enrs[0].student,
              enrollments: enrs,
            }));

            return groupedStudents
              .orderBy(
                [
                  (stu) => stu.lastname.toLowerCase(),
                  (stu) => stu.firstname.toLowerCase(),
                ],
                ["asc", "asc"],
              )
              .map((stu) =>
                dynStyle(
                  updateS,
                  () =>
                    studentFilter.filter(stu) === true ? "" : "display: none;",
                  renderStudentTableRow(opts.data, stu),
                ),
              );
          })}
        </table>
      </section>
    </main>
  );
}

function renderEnrollmentInOneLine(
  filters: advancedStudentFiltersData,
  enr: enrollment,
) {
  const period = filters.periods.find(
    (p) => p.id === enr.lessongroup.startsinperiod,
  );
  const status = enr.enrollment_status;
  return (
    <div style="font-size: 0.85em">
      <a
        href={routes.lessongroup_generalRoute.link({
          lessongroupid: enr.lessongroup.id,
        })}
      >
        {(period ? renderPeriod(period.firstday, period.lastday) + ": " : "") +
          renderTeacherPlusTimePlusWeekday(
            enr.teacher,
            enr.lessongroup.time,
            parseWeekday(enr.lessongroup.weekday),
          ) +
          " " +
          wrapInParenthesesIfNotEmpty(
            renderEnrollmentStatus(status, {
              stoppedlessonson: enr.enrollment_stoppedlessonson,
              startedlessonson: enr.enrollment_startedlessonson,
              lessons: {
                tag: "show_last_lesson",
                lessons: enr.lessons || [],
              },
            }),
          )}
      </a>
    </div>
  );
}

// function renderEnrollmentInMultipleCells(
//   filters: advancedStudentFiltersData,
//   enr: filteredStudent,
// ) {
//   const period = filters.periods.find(
//     (p) => p.id === enr.lessongroup_startsinperiod,
//   );
//   const status = enr.enrollment_status;
//   return [
//     <td>{period ? renderPeriod(period.firstday, period.lastday) : ""}</td>,
//     <td>{enr.enrollment_created.format(ddMMyyyy)}</td>,
//     <td>{enr.instrument_description}</td>,
//     <td>
//       {renderFirstNameLastName({
//         firstname: enr.teacher_firstname,
//         lastname: enr.teacher_lastname,
//       })}
//     </td>,
//     <td>{translateWeekday(parseWeekday(enr.lessongroup_weekday))}</td>,
//     <td>{enr.lessongroup_time.format(HHmm)}</td>,
//     <td style="display: none;">{enr.lessongroup_formuladescription}</td>,
//     <td style="display: none;">{enr.lessongroup_duration}</td>,
//     <td style="display: none;">{enr.lessongroup_numberofplannedlessons}</td>,
//     <td style="display: none;">
//       {enr.enrollment_startedlessonson.format(ddMMyyyy)}
//     </td>,
//     <td style="display: none;">
//       {status === "TrialLesson"
//         ? enr.enrollment_startedlessonson.format(ddMMyyyy)
//         : status === "Stopped"
//         ? getEnrollmentLastLesson({
//             lessons: enr.lessons || [],
//             startedlessonson: enr.enrollment_startedlessonson,
//             stoppedlessonson: enr.enrollment_stoppedlessonson,
//           })?.format(ddMMyyyy) || ""
//         : ""}
//     </td>,
//     <td style="display: none">{enr.room_description || ""}</td>,
//   ];
// }

export function renderStudentTableRow(
  filters: advancedStudentFiltersData,
  stu: student,
) {
  return (
    <tr>
      <td>
        <a
          href={routes.studentOverviewRoute.link({
            studentid: stu.id,
          })}
        >
          {stu.firstname}
        </a>
      </td>
      <td>
        <a
          href={routes.studentOverviewRoute.link({
            studentid: stu.id,
          })}
        >
          {stu.lastname}
        </a>
      </td>
      <td>
        {stu.enrollments.map((e) => renderEnrollmentInOneLine(filters, e))}
      </td>
      <td style="display: none">
        {isNil(stu.thirdpartysellerid)
          ? ""
          : filters.third_party_sellers.find(
              (tps) => tps.id === stu.thirdpartysellerid,
            )?.name || ""}
      </td>
      <td style="display: none">{stu.street}</td>
      <td style="display: none">{stu.number}</td>
      <td style="display: none">{stu.bus}</td>
      <td style="display: none">{stu.zip}</td>
      <td style="display: none">{stu.city}</td>
      <td style="display: none">{stu.birthday?.format(ddMMyyyy) || ""}</td>
      <td style="display: none">
        {stu.birthday
          ? calculateAge({
              birthday: stu.birthday,
              today: LocalDate.now(),
            })
          : ""}
      </td>
      <td style="display: none">{stu.iban || ""}</td>
      <td style="display: none">{stu.name_on_iban || ""}</td>
      <td style="display: none">{stu.comments}</td>
      <td style="display: none">{(stu.emails || [])[0]?.email || ""}</td>
      <td style="display: none">{(stu.emails || [])[1]?.email || ""}</td>
      <td style="display: none">{(stu.emails || [])[2]?.email || ""}</td>
      <td style="display: none">{(stu.emails || [])[3]?.email || ""}</td>
      <td style="display: none">{(stu.phones || [])[0]?.number || ""}</td>
      <td style="display: none">{(stu.phones || [])[1]?.number || ""}</td>
      <td style="display: none">{(stu.phones || [])[2]?.number || ""}</td>
      <td style="display: none">{(stu.phones || [])[3]?.number || ""}</td>
    </tr>
  );
}

export function renderStudentAdvancedHeaderForExport(mode: {
  tag: "grouped_enrollments";
}) {
  return (
    <tr style={"display: none"}>
      <th>{"Voornaam"}</th>
      <th>{"Achternaam"}</th>
      <th>{"Inschrijvingen"}</th>
      <th>{"Vereniging"}</th>
      <th>{"Straat"}</th>
      <th>{"Nummer"}</th>
      <th>{"Bus"}</th>
      <th>{"Postcode"}</th>
      <th>{"Woonplaats"}</th>
      <th>{"Geboortedatum"}</th>
      <th>{"Leeftijd"}</th>
      <th>{"IBAN"}</th>
      <th>{"Naam op IBAN"}</th>
      <th>{"Commentaar"}</th>
      <th>{"Email" + "(1)"}</th>
      <th>{"Email" + "(2)"}</th>
      <th>{"Email" + "(3)"}</th>
      <th>{"Email" + "(4)"}</th>
      <th>{"Telefoon" + "(1)"}</th>
      <th>{"Telefoon" + "(2)"}</th>
      <th>{"Telefoon" + "(3)"}</th>
      <th>{"Telefoon" + "(4)"}</th>
    </tr>
  );
}
